import Link from 'next/link'
import Head from 'next/head'
import Seo, { SITE_URL } from '@comp/seo'
import JsonLd from '@comp/structured-data'
import Layout from '@comp/layout'
import TrustBand from '@comp/home/trust-band'
import FinalCta from '@comp/home/final-cta'
import Container from '@comp/ui/container'
import Section from '@comp/ui/section'
import Eyebrow from '@comp/ui/eyebrow'
import Heading from '@comp/ui/heading'
import Text from '@comp/ui/text'
import Button from '@comp/ui/button'
import { Localized } from '@lib/glossary/types'

interface Plan {
  name: string
  eyebrow: Localized
  price: Localized
  priceNote: Localized
  description: Localized
  features: Localized[]
  cta: { label: Localized; href: string; variant: 'primary' | 'outline' }
  highlight: boolean
}

const plans: Plan[] = [
  {
    name: 'Monosign',
    eyebrow: { en: 'IAM + IGA', tr: 'IAM + IGA' },
    price: { en: 'From $4', tr: '4 $’dan başlayan' },
    priceNote: { en: 'per user / month', tr: 'kullanıcı / ay' },
    description: {
      en: 'SSO, MFA, user provisioning, password management, and lifecycle automation: one product, one price.',
      tr: 'SSO, MFA, kullanıcı sağlama, parola yönetimi ve yaşam döngüsü otomasyonu: tek ürün, tek fiyat.',
    },
    features: [
      { en: 'Single Sign-On & MFA', tr: 'Tek oturum açma (SSO) ve MFA' },
      {
        en: 'User provisioning & lifecycle workflows',
        tr: 'Kullanıcı sağlama ve yaşam döngüsü iş akışları',
      },
      { en: 'Password management', tr: 'Parola yönetimi' },
      { en: '7,000+ pre-built integrations', tr: '7.000+ hazır entegrasyon' },
      {
        en: 'Audit trails & compliance reports',
        tr: 'Denetim izleri ve uyumluluk raporları',
      },
      { en: 'Customer support included', tr: 'Müşteri desteği dahil' },
    ],
    cta: {
      label: { en: 'Start free trial →', tr: 'Ücretsiz deneme başlat →' },
      href: '/try/now',
      variant: 'primary',
    },
    highlight: true,
  },
  {
    name: 'Monopam',
    eyebrow: { en: 'Privileged Access', tr: 'Ayrıcalıklı Erişim' },
    price: { en: 'Per session', tr: 'Oturum başına' },
    priceNote: {
      en: 'concurrent-session licensing',
      tr: 'eşzamanlı oturum lisanslama',
    },
    description: {
      en: 'Pay for concurrent privileged sessions, not per target, not per vault, not per feature.',
      tr: 'Eşzamanlı ayrıcalıklı oturumlar için ödeyin; hedef başına, kasa başına veya özellik başına değil.',
    },
    features: [
      { en: 'Encrypted credential vault', tr: 'Şifreli kimlik bilgisi kasası' },
      {
        en: 'Session recording (video + keystroke)',
        tr: 'Oturum kaydı (video + tuş vuruşu)',
      },
      {
        en: 'Just-in-time access & approvals',
        tr: 'Tam zamanında erişim ve onaylar',
      },
      { en: 'Password rotation', tr: 'Parola rotasyonu' },
      {
        en: 'Browser-based RDP / SSH / DB access',
        tr: 'Tarayıcı tabanlı RDP / SSH / veritabanı erişimi',
      },
      {
        en: 'No per-target or add-on fees',
        tr: 'Hedef başına ücret veya ek modül ücreti yok',
      },
    ],
    cta: {
      label: { en: 'Talk to sales', tr: 'Satışla görüşün' },
      href: '/contact',
      variant: 'outline',
    },
    highlight: false,
  },
  {
    name: 'Monosync',
    eyebrow: { en: 'Identity Governance', tr: 'Kimlik Yönetişimi' },
    price: { en: 'Custom', tr: 'Özel teklif' },
    priceNote: {
      en: 'sized to your identity estate',
      tr: 'kimlik varlığınızın ölçeğine göre',
    },
    description: {
      en: 'Directory sync, entitlement mapping, and access reviews priced for the scale of your directories.',
      tr: 'Dizin senkronizasyonu, yetki eşleme ve erişim gözden geçirmeleri; dizinlerinizin ölçeğine göre fiyatlandırılır.',
    },
    features: [
      {
        en: 'Bidirectional directory sync',
        tr: 'Çift yönlü dizin senkronizasyonu',
      },
      { en: 'Entitlement discovery & mapping', tr: 'Yetki keşfi ve eşleme' },
      {
        en: 'Access reviews & certification',
        tr: 'Erişim gözden geçirme ve sertifikasyon',
      },
      {
        en: 'Separation-of-duties policies',
        tr: 'Görevler ayrılığı politikaları',
      },
      {
        en: 'SOC 2 / ISO 27001 / KVKK reports',
        tr: 'SOC 2 / ISO 27001 / KVKK raporları',
      },
      { en: 'Custom REST connectors', tr: 'Özel REST bağlayıcıları' },
    ],
    cta: {
      label: { en: 'Talk to sales', tr: 'Satışla görüşün' },
      href: '/contact',
      variant: 'outline',
    },
    highlight: false,
  },
]

const included: { title: Localized; body: Localized }[] = [
  {
    title: { en: 'Support included', tr: 'Destek dahil' },
    body: {
      en: 'Customer support is part of every plan, never a separate SKU.',
      tr: 'Müşteri desteği her planın parçası, asla ayrı bir kalem değil.',
    },
  },
  {
    title: { en: 'Self-hosted or cloud', tr: 'Kendi sunucunuzda veya bulutta' },
    body: {
      en: 'Same product, same price, your choice of deployment.',
      tr: 'Aynı ürün, aynı fiyat; dağıtım tercihi sizin.',
    },
  },
  {
    title: { en: 'All core features', tr: 'Tüm temel özellikler' },
    body: {
      en: 'No feature gates inside a product. What you see is what ships.',
      tr: 'Ürün içinde özellik kilidi yok. Ne görüyorsanız onu alırsınız.',
    },
  },
  {
    title: { en: '30-day free trial', tr: '30 gün ücretsiz deneme' },
    body: {
      en: 'A fully-loaded tenant in five minutes. No credit card required.',
      tr: 'Beş dakikada tam donanımlı bir tenant. Kredi kartı gerekmez.',
    },
  },
]

const faqs: { q: Localized; a: Localized }[] = [
  {
    q: {
      en: 'How does Monosign per-user pricing work?',
      tr: 'Monosign kullanıcı başına fiyatlandırma nasıl çalışır?',
    },
    a: {
      en: 'You pay for active users, starting from $4 per user per month. Volume tiers bring the per-user price down as you grow; talk to sales for a quote matched to your user count.',
      tr: 'Aktif kullanıcılar için ödersiniz; fiyat kullanıcı başına aylık 4 $’dan başlar. Hacim kademeleri büyüdükçe kullanıcı başına fiyatı düşürür; kullanıcı sayınıza uygun bir teklif için satışla görüşün.',
    },
  },
  {
    q: {
      en: 'What does per-concurrent-session mean for Monopam?',
      tr: 'Monopam’da eşzamanlı oturum başına lisanslama ne anlama gelir?',
    },
    a: {
      en: 'Monopam is licensed by how many privileged sessions run at the same time, not by how many admins, servers, or credentials you have. Most teams need far fewer concurrent sessions than total admins, which is why this model usually costs less than per-user PAM pricing.',
      tr: 'Monopam, aynı anda kaç ayrıcalıklı oturumun çalıştığına göre lisanslanır; kaç yöneticiniz, sunucunuz veya kimlik bilginiz olduğuna göre değil. Çoğu ekibin eşzamanlı oturum ihtiyacı toplam yönetici sayısından çok daha azdır; bu yüzden bu model genellikle kullanıcı başına PAM fiyatlandırmasından daha uygun çıkar.',
    },
  },
  {
    q: {
      en: 'Is support or onboarding an extra cost?',
      tr: 'Destek veya devreye alma ek ücretli mi?',
    },
    a: {
      en: 'No. Customer support is included in every plan, and most teams deploy in minutes without paid professional services.',
      tr: 'Hayır. Müşteri desteği her plana dahildir ve çoğu ekip ücretli profesyonel hizmet almadan dakikalar içinde devreye alır.',
    },
  },
  {
    q: {
      en: 'Does self-hosted deployment cost more?',
      tr: 'Kendi sunucunuzda kurulum daha mı pahalı?',
    },
    a: {
      en: 'No. Self-hosted and cloud deployments are priced the same; you choose where your data lives.',
      tr: 'Hayır. Kendi sunucunuzda ve bulut dağıtımları aynı fiyattadır; verinizin nerede duracağını siz seçersiniz.',
    },
  },
  {
    q: {
      en: 'Are there volume or multi-product discounts?',
      tr: 'Hacim veya çoklu ürün indirimi var mı?',
    },
    a: {
      en: 'Yes. Combining Monosign, Monopam, and Monosync (or growing past volume thresholds) reduces the effective price. Contact sales for a tailored quote.',
      tr: 'Evet. Monosign, Monopam ve Monosync’i birlikte almak (veya hacim eşiklerini aşmak) efektif fiyatı düşürür. Size özel bir teklif için satışla iletişime geçin.',
    },
  },
]

const ui = {
  seoTitle: { en: 'Pricing | Monofor', tr: 'Fiyatlandırma | Monofor' },
  seoDescription: {
    en: 'Monofor pricing: Monosign IAM from $4 per user per month, Monopam PAM per concurrent session, support included, self-hosted or cloud. 30-day free trial.',
    tr: 'Monofor fiyatlandırması: Monosign IAM kullanıcı başına aylık 4 $’dan, Monopam PAM eşzamanlı oturum başına; destek dahil, kendi sunucunuzda veya bulutta. 30 gün ücretsiz deneme.',
  },
  productDescription: {
    en: 'IAM platform: SSO, MFA, user provisioning, password management, and lifecycle automation.',
    tr: 'IAM platformu: SSO, MFA, kullanıcı sağlama, parola yönetimi ve yaşam döngüsü otomasyonu.',
  },
  eyebrow: { en: 'Pricing', tr: 'Fiyatlandırma' },
  titleLine1: { en: 'Honest pricing.', tr: 'Dürüst fiyatlandırma.' },
  titleLine2: { en: 'No decoder ring required.', tr: 'Şifre çözücü gerekmez.' },
  lede: {
    en: 'Plans start at $4 per user per month. Support included, deployment your choice, and no hidden add-ons at renewal time.',
    tr: 'Planlar kullanıcı başına aylık 4 $’dan başlar. Destek dahil, dağıtım tercihi sizin; yenileme zamanında gizli ek ücret yok.',
  },
  salesNotePrefix: {
    en: 'Combining products? Multi-product and volume discounts apply; ',
    tr: 'Ürünleri birlikte mi alacaksınız? Çoklu ürün ve hacim indirimleri uygulanır; ',
  },
  salesNoteLink: { en: 'talk to sales', tr: 'satışla görüşün' },
  includedEyebrow: { en: 'Every plan includes', tr: 'Her plana dahil' },
  includedHeading: {
    en: 'What you see is what ships.',
    tr: 'Ne görüyorsanız onu alırsınız.',
  },
  faqEyebrow: { en: 'FAQ', tr: 'SSS' },
  faqHeading: {
    en: 'Pricing questions, answered.',
    tr: 'Fiyatlandırma soruları, yanıtlarıyla.',
  },
}

function pick(v: Localized, locale: string) {
  return locale === 'tr' ? v.tr || v.en : v.en
}

type Props = { locale: string }

export default function PricingPage({ locale }: Props) {
  const basePath = locale === 'tr' ? '/tr' : ''
  const p = (v: Localized) => pick(v, locale)

  return (
    <Layout>
      <Seo
        title={p(ui.seoTitle)}
        description={p(ui.seoDescription)}
        path={`${basePath}/pricing`}
      />
      <Head>
        <link rel="alternate" hrefLang="en" href={`${SITE_URL}/pricing`} />
        <link rel="alternate" hrefLang="tr" href={`${SITE_URL}/tr/pricing`} />
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${SITE_URL}/pricing`}
        />
      </Head>
      <JsonLd
        id="pricing"
        data={{
          '@type': 'Product',
          name: 'Monosign',
          description: p(ui.productDescription),
          brand: { '@type': 'Brand', name: 'Monofor' },
          offers: {
            '@type': 'AggregateOffer',
            lowPrice: '4',
            priceCurrency: 'USD',
            offerCount: '3',
            url: `${SITE_URL}${basePath}/pricing`,
          },
        }}
      />
      <JsonLd
        id="pricing-faq"
        data={{
          '@type': 'FAQPage',
          mainEntity: faqs.map((f) => ({
            '@type': 'Question',
            name: p(f.q),
            acceptedAnswer: { '@type': 'Answer', text: p(f.a) },
          })),
        }}
      />
      <main className="bg-surface text-ink-primary">
        {/* Hero */}
        <Section tone="default" density="compact" className="relative overflow-hidden">
          <BackgroundField />
          <Container size="xl" className="relative">
            <div className="max-w-3xl">
              <Eyebrow tone="brand">{p(ui.eyebrow)}</Eyebrow>
              <div className="mt-6">
                <Heading as="h1" size="display-xl">
                  {p(ui.titleLine1)}
                  <br />
                  {p(ui.titleLine2)}
                </Heading>
              </div>
              <div className="mt-8 max-w-xl">
                <Text size="xl" tone="secondary">
                  {p(ui.lede)}
                </Text>
              </div>
            </div>
          </Container>
        </Section>

        {/* Plans */}
        <Section tone="subtle" density="default">
          <Container size="xl">
            <div className="grid gap-6 lg:grid-cols-3">
              {plans.map((plan) => (
                <div
                  key={plan.name}
                  className={`flex h-full flex-col rounded-3xl border bg-surface p-8 lg:p-10 ${
                    plan.highlight
                      ? 'border-brand-300 shadow-card-hover ring-1 ring-brand-300'
                      : 'border-line-subtle shadow-card'
                  }`}
                >
                  <div className="text-eyebrow font-semibold uppercase tracking-[0.12em] text-brand-700">
                    {p(plan.eyebrow)}
                  </div>
                  <h2 className="mt-3 text-display-sm font-medium text-ink-primary">
                    {plan.name}
                  </h2>
                  <div className="mt-6">
                    <div className="text-display-md font-medium text-ink-primary">
                      {p(plan.price)}
                    </div>
                    <div className="mt-1 text-body-sm text-ink-tertiary">
                      {p(plan.priceNote)}
                    </div>
                  </div>
                  <p className="mt-4 text-body-md text-ink-secondary">
                    {p(plan.description)}
                  </p>
                  <ul className="mt-6 flex-grow space-y-3">
                    {plan.features.map((feature) => (
                      <li
                        key={feature.en}
                        className="flex items-start gap-2.5 text-body-sm text-ink-secondary"
                      >
                        <CheckGlyph className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-600" />
                        {p(feature)}
                      </li>
                    ))}
                  </ul>
                  <div className="mt-8">
                    <Button
                      href={plan.cta.href}
                      variant={plan.cta.variant}
                      size="md"
                      className="w-full"
                    >
                      {p(plan.cta.label)}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8 text-center">
              <Text size="sm" tone="tertiary">
                {p(ui.salesNotePrefix)}
                <Link
                  href="/contact"
                  className="text-brand-700 underline-offset-4 hover:underline"
                >
                  {p(ui.salesNoteLink)}
                </Link>
                .
              </Text>
            </div>
          </Container>
        </Section>

        {/* Included in every plan */}
        <Section tone="default" density="default">
          <Container size="xl">
            <div className="max-w-3xl">
              <Eyebrow tone="accent">{p(ui.includedEyebrow)}</Eyebrow>
              <Heading as="h2" size="display-lg" className="mt-5">
                {p(ui.includedHeading)}
              </Heading>
            </div>
            <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
              {included.map((item) => (
                <div
                  key={item.title.en}
                  className="rounded-2xl border border-line-subtle bg-surface-subtle p-6"
                >
                  <div className="text-body-md font-medium text-ink-primary">
                    {p(item.title)}
                  </div>
                  <Text size="sm" tone="tertiary" className="mt-2">
                    {p(item.body)}
                  </Text>
                </div>
              ))}
            </div>
          </Container>
        </Section>

        <TrustBand />

        {/* FAQ */}
        <Section tone="subtle" density="default">
          <Container size="md">
            <Eyebrow tone="brand">{p(ui.faqEyebrow)}</Eyebrow>
            <Heading as="h2" size="display-lg" className="mt-5">
              {p(ui.faqHeading)}
            </Heading>
            <dl className="mt-12 space-y-4">
              {faqs.map((faq) => (
                <div
                  key={faq.q.en}
                  className="rounded-2xl border border-line-subtle bg-surface p-6 lg:p-8"
                >
                  <dt className="text-body-lg font-medium text-ink-primary">
                    {p(faq.q)}
                  </dt>
                  <dd className="mt-3 text-body-md text-ink-secondary">
                    {p(faq.a)}
                  </dd>
                </div>
              ))}
            </dl>
          </Container>
        </Section>

        <FinalCta />
      </main>
    </Layout>
  )
}

function BackgroundField() {
  return (
    <div aria-hidden className="pointer-events-none absolute inset-0">
      <div className="absolute -top-32 -right-24 h-[640px] w-[640px] rounded-full bg-brand-100/60 blur-3xl" />
      <div className="absolute top-40 -left-32 h-[420px] w-[420px] rounded-full bg-accent-100/50 blur-3xl" />
    </div>
  )
}

function CheckGlyph({ className = '' }) {
  return (
    <svg className={className} viewBox="0 0 16 16" fill="none">
      <circle cx="8" cy="8" r="8" fill="currentColor" fillOpacity="0.12" />
      <path
        d="M4.6 8.2 7 10.6 11.4 5.8"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

export async function getStaticProps({ locale }: { locale?: string }) {
  return { props: { locale: locale ?? 'en' } }
}
